/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import { createIntelReport, deleteIntelReport, listIntelReports } from './intelligenceReportApi';
import { normalizeReport, normalizeReportStatus, toCount } from './utils/normalizeReport';
import { INTEL_MODULES } from './static/constants';

/**
 * Intelligence Report index state — the holistic list across every intelligence module.
 * Module-specific state stays in each module's own slice (CompetitiveIntel, Audiences).
 */
const initialState = {
  reports: [],
  reportsLoading: false,
  // True once GET /intel-reports has answered — until then the list shows its loader even if a
  // just-created (optimistic) report is already in `reports`.
  reportsLoaded: false,
  reportsError: null,
};

// An optimistic row (added by the Create wizard) is the same report as a server row when the
// product name and URL match.
const sameProduct = (a, b) =>
  String(a.name).toLowerCase() === String(b.name).toLowerCase() &&
  String(a.url || '').toLowerCase() === String(b.url || '').toLowerCase();

const IntelligenceReportSlice = createSlice({
  name: 'IntelligenceReport',
  initialState,
  reducers: {
    // Replaces the list with the server's, keeping optimistic rows the server doesn't have yet.
    setReports(state, action) {
      const fetched = action.payload;
      const pending = state.reports.filter(
        (row) => row.optimistic && !fetched.some((serverRow) => sameProduct(serverRow, row))
      );
      state.reports = [...pending, ...fetched];
      state.reportsLoaded = true;
    },
    /**
     * Insert or patch one row (already normalized).
     */
    upsertReport(state, action) {
      const row = action.payload;
      const index = state.reports.findIndex((existing) => String(existing.id) === String(row.id));
      if (index >= 0) {
        state.reports[index] = { ...state.reports[index], ...row };
      } else {
        state.reports.unshift(row);
      }
    },
    /**
     * Live status push (Reverb `intel-report.{id}` → `.status.updated`): only the sub-reports
     * present in the payload change (`audienceReport` / `competitiveReport` / `culturalReport`,
     * each `{ id, status }`, plus the module's count when sent); an absent key means unchanged.
     * Other fields come from REST.
     */
    reportLayerStatusChanged(state, action) {
      const push = action.payload || {};
      const row = state.reports.find((report) => String(report.id) === String(push.intelReportId));
      if (!row) return;
      INTEL_MODULES.forEach(({ key, reportField, countField }) => {
        const change = push[reportField];
        if (!change || typeof change !== 'object') return;
        const layer = row.layers[key] || {};
        row.layers[key] = {
          ...layer,
          id: change.id ?? layer.id ?? null,
          requested: true,
          status: normalizeReportStatus(change.status),
          count:
            countField && change[countField] != null
              ? toCount(change[countField])
              : layer.count ?? null,
        };
      });
    },
    removeReport(state, action) {
      state.reports = state.reports.filter((row) => String(row.id) !== String(action.payload));
    },
    setReportsLoading(state, action) {
      state.reportsLoading = action.payload;
    },
    setReportsError(state, action) {
      state.reportsError = action.payload;
    },
  },
});

export const {
  setReports,
  upsertReport,
  reportLayerStatusChanged,
  removeReport,
  setReportsLoading,
  setReportsError,
} = IntelligenceReportSlice.actions;

// The list rows: `data: [...]` (the shared contract) or a paginated `data: { data: [...] }`.
const rowsOf = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.data?.data)) return resp.data.data;
  return null;
};

/** Loads the holistic report list (GET /intel-reports). Resolves to the normalized rows ([] on failure). */
export const fetchReports = () => (dispatch) => {
  dispatch(setReportsLoading(true));
  dispatch(setReportsError(null));
  return listIntelReports()
    .then((resp) => {
      const rows = isApiSuccess(resp) ? rowsOf(resp) : null;
      if (!rows) {
        dispatch(setReportsError(resp?.message || resp?.error || "Couldn't load reports."));
        return [];
      }
      const normalized = rows.map(normalizeReport);
      dispatch(setReports(normalized));
      return normalized;
    })
    .catch(() => {
      dispatch(setReportsError("Couldn't load reports."));
      return [];
    })
    .finally(() => dispatch(setReportsLoading(false)));
};

const submitErrorOf = (resp) => {
  const fieldErrors =
    resp?.errors && typeof resp.errors === 'object'
      ? Object.values(resp.errors)
          .flat()
          .filter(Boolean)
          .join(' ')
      : '';
  return (
    fieldErrors ||
    resp?.message ||
    resp?.error ||
    "We couldn't create the report. Please try again."
  );
};

/**
 * POST /intel-reports — submit the wizard's report. On success the report is added to the list:
 * from the response when it carries the report, otherwise the caller's `fallbackRow` (already
 * normalized). Rows stay `optimistic` until the list endpoint returns them.
 *
 * @param {object} payload - the POST /intel-reports body
 * @param {object} fallbackRow - normalized row to show when the response has no report
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const submitIntelReport = (payload, fallbackRow) => async (dispatch) => {
  const resp = await createIntelReport(payload);
  const ok = isApiSuccess(resp) || resp?.httpStatus === 201 || resp?.status === 201;
  if (!ok) return { ok: false, error: submitErrorOf(resp) };

  const created = resp?.data && typeof resp.data === 'object' && resp.data.id != null;
  dispatch(
    upsertReport({ ...(created ? normalizeReport(resp.data) : fallbackRow), optimistic: true })
  );
  return { ok: true };
};

// A DELETE can succeed with a JSON envelope, a `{ deleted: true }` body, or an empty 2xx
// (No Content) — same rule as Audits.slice.js.
const isDeleteSuccess = (resp) =>
  isApiSuccess(resp) ||
  resp?.data?.deleted === true ||
  (resp?.httpStatus >= 200 && resp?.httpStatus < 300);

/** DELETE /intel-reports/{id}, then drop the row. Resolves { ok, error }. */
export const deleteReport = (id) => (dispatch) =>
  deleteIntelReport(id)
    .then((resp) => {
      if (!isDeleteSuccess(resp)) {
        return {
          ok: false,
          error: resp?.message || resp?.error || "Couldn't delete the report. Please try again.",
        };
      }
      dispatch(removeReport(id));
      return { ok: true };
    })
    .catch(() => ({ ok: false, error: "Couldn't delete the report. Please try again." }));

export default IntelligenceReportSlice.reducer;
